/**
 * BasicInfoTab — Campus details display with optional location map.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, Marker, Popup, LayersControl } from 'react-leaflet';
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer';
import {formatPhone, convertIsoToLocal, convertIsoToLocalDate} from '../../../../../libs.jsx';
import { GOOGLE_MAPS_API_KEY as GOOGLE_API_KEY } from '../../../../../constants/mapsConfig.js';

const BasicInfoTab = ({
  campusData, corporateName, corporateId, timezoneName,
  editRights, onEdit,
}) => {
  const mapCenter = useMemo(() => {
    const lat = parseFloat(campusData?.campus_latitude) || 0;
    const lng = parseFloat(campusData?.campus_longitude) || 0;
    return (lat !== 0 || lng !== 0) ? [lat, lng] : null;
  }, [campusData?.campus_latitude, campusData?.campus_longitude]);

  return (
    <>
      {/* Campus Details */}
      <div className="pp-section">
        <div className="pp-section__header">
          <h3 className="pp-section__title">Campus Details</h3>
          <div className="pp-section__actions">
            {editRights && (
              <button className="dp-btn dp-btn--secondary dp-btn--sm" onClick={onEdit}>
                ✎ Edit
              </button>
            )}
          </div>
        </div>
        <div className="pp-section__body">
          <div className="pp-field-grid">
            <div className="pp-field">
              <span className="pp-field__label">Campus ID</span>
              <span className="pp-field__value">{campusData?.campus_id || '—'}</span>
            </div>
            <div className="pp-field">
              <span className="pp-field__label">Campus GUID</span>
              <span className="pp-field__value pp-field__value--mono">
                {campusData?.campus_guid || '—'}
              </span>
            </div>
            <div className="pp-field">
              <span className="pp-field__label">Campus Name</span>
              <span className="pp-field__value">{campusData?.campus_name || '—'}</span>
            </div>
            <div className="pp-field">
              <span className="pp-field__label">Corporation</span>
              <span className="pp-field__value">
                {corporateId ? (
                  <Link to={`/profiles/corporate/${corporateId}`} className="pp-field__link">
                    {corporateName || `Corp #${corporateId}`}
                  </Link>
                ) : (
                  '—'
                )}
              </span>
            </div>
            <div className="pp-field">
              <span className="pp-field__label">Street Address</span>
              <span className="pp-field__value">{campusData?.campus_street_address || '—'}</span>
            </div>
            <div className="pp-field">
              <span className="pp-field__label">Country</span>
              <span className="pp-field__value">{campusData?.campus_country || '—'}</span>
            </div>
            <div className="pp-field">
              <span className="pp-field__label">City</span>
              <span className="pp-field__value">{campusData?.campus_city || '—'}</span>
            </div>
            <div className="pp-field">
              <span className="pp-field__label">State</span>
              <span className="pp-field__value">{campusData?.campus_state || '—'}</span>
            </div>
            <div className="pp-field">
              <span className="pp-field__label">Zip Code</span>
              <span className="pp-field__value">{campusData?.campus_zip || '—'}</span>
            </div>
            <div className="pp-field">
              <span className="pp-field__label">Timezone</span>
              <span className="pp-field__value">{timezoneName || '—'}</span>
            </div>
            <div className="pp-field">
              <span className="pp-field__label">Email</span>
              <span className="pp-field__value">{campusData?.campus_email || '—'}</span>
            </div>
            <div className="pp-field">
              <span className="pp-field__label">Phone</span>
              <span className="pp-field__value">
                {campusData?.campus_phone ? formatPhone(campusData.campus_phone) : '—'}
              </span>
            </div>
            <div className="pp-field">
              <span className="pp-field__label">Service Plan Expiry</span>
              <span className="pp-field__value">
                {campusData?.date_service_plan_expire
                  ? convertIsoToLocalDate(campusData.date_service_plan_expire)
                  : '—'}
              </span>
            </div>
            <div className="pp-field">
              <span className="pp-field__label">Created On</span>
              <span className="pp-field__value">
                {campusData?.create_time
                  ? convertIsoToLocalDate(campusData.create_time)
                  : '—'}
              </span>
            </div>
          </div>

          {/* Location map — only if lat/lng are available */}
          {mapCenter && (
            <div className="cpp-location-map">
              <MapContainer
                center={mapCenter}
                zoom={16}
                scrollWheelZoom={true}
                style={{ height: '100%', width: '100%' }}
              >
                <LayersControl position="topright">
                  <LayersControl.BaseLayer checked name="Roadmap">
                    <ReactLeafletGoogleLayer apiKey={GOOGLE_API_KEY} type="roadmap" />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="Satellite">
                    <ReactLeafletGoogleLayer apiKey={GOOGLE_API_KEY} type="satellite" />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="Terrain">
                    <ReactLeafletGoogleLayer apiKey={GOOGLE_API_KEY} type="terrain" />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="Hybrid">
                    <ReactLeafletGoogleLayer apiKey={GOOGLE_API_KEY} type="hybrid" />
                  </LayersControl.BaseLayer>
                </LayersControl>
                <Marker position={mapCenter}>
                  <Popup>
                    <strong>{campusData?.campus_name || 'Campus Location'}</strong>
                    <br />
                    {mapCenter[0].toFixed(6)}, {mapCenter[1].toFixed(6)}
                  </Popup>
                </Marker>
              </MapContainer>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BasicInfoTab;
